import React, { useState } from "react";
import { apiClient } from "../utils/api";
import { prefectures } from "../utils/prefectures";

/**
 * 住所データの型
 */
export interface AddressData {
	postalCode: string; // 郵便番号
	prefecture: string; // 都道府県
	city: string; // 市区町村
	streetAddress: string; // 番地
	buildingName: string; // 建物名
}

/**
 * AddressInput コンポーネントに渡す props の型
 */
interface AddressInputProps {
	addressData: AddressData;
	onChange: (updatedAddress: AddressData) => void;
	required?: boolean; // 必須項目かどうかのフラグ
}

/**
 * 郵便番号フォーマット関数
 * 例: "1234567" → "123-4567"
 */
const formatPostalCode = (code: string): string => {
	const numeric = code.replace(/[^\d]/g, "");
	if (numeric.length > 3) {
		return numeric.slice(0, 3) + "-" + numeric.slice(3);
	}
	return numeric;
};

/**
 * 住所入力コンポーネント
 * 郵便番号のオートコンプリート機能を内包
 * - props で受け取った addressData を表示・変更
 * - onChange コールバックで親コンポーネントに変更を通知
 */
const AddressInput: React.FC<AddressInputProps> = ({
	addressData,
	onChange,
	required = false,
}) => {
	const [isSearchingAddress, setIsSearchingAddress] = useState(false);

	// 郵便番号の入力時ハンドラ（7桁で API を叩いて住所検索を行う例）
	const handlePostalCodeChange = async (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const rawValue = e.target.value;
		// 入力された文字から数字だけ取り出し
		const numericValue = rawValue.replace(/[^\d]/g, "");

		// ひとまず郵便番号の state だけ先に更新
		onChange({
			...addressData,
			postalCode: numericValue,
		});

		// 7桁になったら住所検索
		if (numericValue.length === 7) {
			setIsSearchingAddress(true);
			try {
				// 実際のAPIパスは環境に合わせて変更してください
				// ここでは例として /postal-code/search?postal_code=XXXXXXX を利用
				const response = await apiClient.get(
					`/postal-code/search?postal_code=${numericValue}`
				);
				const found = response.data;

				onChange({
					...addressData,
					postalCode: numericValue,
					prefecture: found.prefecture || "",
					city: found.city || "",
					streetAddress: found.street_address || "",
				});
			} catch (error) {
				console.error("住所の検索に失敗しました:", error);
				// 必要に応じてエラーメッセージを表示
			} finally {
				setIsSearchingAddress(false);
			}
		}
	};

	return (
		<div className="space-y-4">
			<div className="flex flex-col gap-4 sm:flex-row">
				{/* 郵便番号 */}
				<div className="w-full sm:w-1/2">
					<label
						htmlFor="postal_code"
						className="block text-sm font-medium text-gray-700 dark:text-gray-300"
					>
						郵便番号{" "}
						{required && (
							<span className="text-red-500 dark:text-red-400">*</span>
						)}
					</label>
					<div className="relative">
						<input
							type="text"
							id="postal_code"
							name="postal_code"
							value={formatPostalCode(addressData.postalCode)}
							onChange={handlePostalCodeChange}
							placeholder="1234567"
							maxLength={8} // ハイフン込みで8文字
							className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
							required={required}
						/>
						{isSearchingAddress && (
							<div className="absolute right-2 top-1/2 transform -translate-y-1/2">
								<svg
									className="animate-spin h-5 w-5 text-indigo-500"
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
								>
									<circle
										className="opacity-25"
										cx="12"
										cy="12"
										r="10"
										stroke="currentColor"
										strokeWidth="4"
									></circle>
									<path
										className="opacity-75"
										fill="currentColor"
										d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4
                     zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135
                     5.824 3 7.938l3-2.647z"
									></path>
								</svg>
							</div>
						)}
					</div>
				</div>

				{/* 都道府県 */}
				<div className="w-full sm:w-1/2">
					<label
						htmlFor="prefecture"
						className="block text-sm font-medium text-gray-700 dark:text-gray-300"
					>
						都道府県{" "}
						{required && (
							<span className="text-red-500 dark:text-red-400">*</span>
						)}
					</label>
					<select
						id="prefecture"
						name="prefecture"
						value={addressData.prefecture}
						onChange={(e) =>
							onChange({
								...addressData,
								prefecture: e.target.value,
							})
						}
						className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
						required={required}
					>
						<option value="">選択してください</option>
						{prefectures.map((pref) => (
							<option key={pref} value={pref}>
								{pref}
							</option>
						))}
					</select>
				</div>
			</div>

			<div className="flex flex-col gap-4 sm:flex-row">
				{/* 市区町村 */}
				<div className="w-full sm:w-1/2">
					<label
						htmlFor="city"
						className="block text-sm font-medium text-gray-700 dark:text-gray-300"
					>
						市区町村{" "}
						{required && (
							<span className="text-red-500 dark:text-red-400">*</span>
						)}
					</label>
					<input
						type="text"
						id="city"
						name="city"
						value={addressData.city}
						onChange={(e) =>
							onChange({
								...addressData,
								city: e.target.value,
							})
						}
						className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
						required={required}
					/>
				</div>

				{/* 番地 */}
				<div className="w-full sm:w-1/2">
					<label
						htmlFor="streetAddress"
						className="block text-sm font-medium text-gray-700 dark:text-gray-300"
					>
						番地{" "}
						{required && (
							<span className="text-red-500 dark:text-red-400">*</span>
						)}
					</label>
					<input
						type="text"
						id="streetAddress"
						name="streetAddress"
						value={addressData.streetAddress}
						onChange={(e) =>
							onChange({
								...addressData,
								streetAddress: e.target.value,
							})
						}
						className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
						required={required}
					/>
				</div>
			</div>

			{/* 建物名 */}
			<div>
				<label
					htmlFor="buildingName"
					className="block text-sm font-medium text-gray-700 dark:text-gray-300"
				>
					建物名
				</label>
				<input
					type="text"
					id="buildingName"
					name="buildingName"
					value={addressData.buildingName}
					onChange={(e) =>
						onChange({
							...addressData,
							buildingName: e.target.value,
						})
					}
					className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
					required={required}
				/>
			</div>
		</div>
	);
};

export default AddressInput;
